import {
  Button,
  DatePicker,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { getLocalTimeZone, today } from '@internationalized/date';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';

interface DateModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

export function DateModalSurgery({ isOpen, onClose, title }: DateModalProps) {
  const { t } = useTranslation('home');

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();
  const { dischargeDate } = usePatientDates();

  const [date, setDate] = useState<null | string>(null);
  const [error, setError] = useState('');

  const validateDate = () => {
    let valid = false;

    if (!dayjs(date).isValid()) {
      setError(t('common.error.blank_input'));
      return false;
    }

    valid =
      dayjs(date).isSameOrBefore(dischargeDate) ||
      !dayjs(dischargeDate).isValid();

    if (!valid) {
      setError(
        t('datemodal.error.invalid_surgery', {
          dischargeStr: dayjs(dischargeDate).format('LL'),
        })
      );
    }

    return valid;
  };

  const handleSubmit = async () => {
    const valid = validateDate();
    if (!valid) return;

    const params = {
      surgeryDate: dayjs(date).locale('en').format('MM/DD/YYYY'),
    };

    const result = await updatePatient(params).unwrap();
    if (result.success) {
      setError('');
      onClose();
    } else {
      setError(t('common.error.server'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <DatePicker
            label={t('common.input.date')}
            errorMessage={error}
            isInvalid={!_.isEmpty(error)}
            minValue={today(getLocalTimeZone()).subtract({ days: 60 })}
            onChange={(date) => setDate(_.toString(date))}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            {t('common.action.cancel')}
          </Button>
          <Button color="primary" isLoading={isLoading} onPress={handleSubmit}>
            {t('common.action.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function DateModalDischarge({ isOpen, onClose, title }: DateModalProps) {
  const { t } = useTranslation('home');

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();
  const { surgeryDate } = usePatientDates();

  const [date, setDate] = useState<null | string>(null);
  const [error, setError] = useState('');

  const validateDate = () => {
    let valid = false;

    if (!dayjs(date).isValid()) {
      setError(t('common.error.blank_input'));
      return false;
    }

    valid = dayjs(date).isSameOrAfter(surgeryDate);

    if (!valid) {
      setError(
        t('datemodal.error.invalid_discharge', {
          surgeryStr: dayjs(surgeryDate).format('LL'),
        })
      );
    }

    return valid;
  };

  const handleSubmit = async () => {
    const valid = validateDate();
    if (!valid) return;

    const params = {
      dischargeDate: dayjs(date).locale('en').format('MM/DD/YYYY'),
    };

    const result = await updatePatient(params).unwrap();
    if (result.success) {
      setError('');
      onClose();
    } else {
      setError(t('common.error.server'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <DatePicker
            label={t('common.input.date')}
            errorMessage={error}
            isInvalid={!_.isEmpty(error)}
            onChange={(date) => setDate(_.toString(date))}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            {t('common.action.cancel')}
          </Button>
          <Button color="primary" isLoading={isLoading} onPress={handleSubmit}>
            {t('common.action.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
